import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { Frame, MEDIA_RADIUS } from "@/components/public/Shot";

const DESCRIPTION = "A recording of BrynAI in the app. A student attaches a homework photo of a ladder problem and asks for help with Q4. "
  + "BrynAI gives the result first, the top is sliding down at 0.75 m/s, then four numbered steps with the maths written out and a quick check question.";
const CONTROL = {
  playing: { Icon: Pause, label: "Pause", aria: "Pause the demo video" },
  paused: { Icon: Play, label: "Play", aria: "Play the demo video" },
  ended: { Icon: RotateCcw, label: "Watch again", aria: "Watch the demo video again" },
};

// Plays once on its own (silent), then waits for a tap to play again. Anyone who prefers less motion gets a still and a Play button.
export function HeroDemo() {
  const ref = useRef(null);
  const [reduce] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [state, setState] = useState(reduce ? "paused" : "playing");
  useEffect(() => {
    const v = ref.current;
    if (reduce || !v) return;
    v.muted = true;
    v.play().catch(() => setState("paused"));
  }, [reduce]);
  const toggle = () => {
    const v = ref.current;
    if (state === "playing") { v.pause(); return; }
    if (v.ended) v.currentTime = 0;
    v.play().catch(() => setState("paused"));
  };
  const c = CONTROL[state];
  return (
    <Frame>
      <div className="relative">
        <video ref={ref} muted playsInline preload={reduce ? "metadata" : "auto"} width="1600" height="950" onClick={toggle}
          poster={`/home/hero-demo-${reduce ? "end" : "start"}.webp`} aria-describedby="hero-demo-desc"
          onPlay={() => setState("playing")} onPause={(e) => { if (!e.currentTarget.ended) setState("paused"); }} onEnded={() => setState("ended")}
          className={`block w-full h-auto cursor-pointer bg-[#0B0E17] ${MEDIA_RADIUS}`} data-testid="hero-demo-video">
          <source src="/home/hero-demo-1600.mp4" type="video/mp4" media="(min-width: 641px)" />
          <source src="/home/hero-demo-800.mp4" type="video/mp4" />
        </video>
        <p id="hero-demo-desc" className="sr-only">{DESCRIPTION}</p>
        {state !== "playing" && (
          <button type="button" onClick={toggle} aria-label={c.aria} data-testid="hero-demo-play-big"
            className="absolute inset-0 m-auto h-16 w-16 sm:h-20 sm:w-20 grid place-items-center rounded-full bg-sky-400 text-white shadow-[0_16px_40px_-10px_rgba(37,99,255,0.9)] hover:bg-sky-500 hover:scale-105">
            <c.Icon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
          </button>
        )}
        <button type="button" onClick={toggle} aria-label={c.aria} data-testid="hero-demo-toggle"
          className="absolute right-3 bottom-3 sm:right-4 sm:bottom-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-[#0B0E17]/80 backdrop-blur-md px-3.5 py-1.5 text-xs text-neutral-100 hover:bg-[#0B0E17]">
          <c.Icon className="h-3.5 w-3.5" aria-hidden="true" />{c.label}
        </button>
      </div>
    </Frame>
  );
}
